/**
 * «Что в выписке» — §3.2 дополнения о сверке, D260.
 *
 * Раскладку платежей по накладным строит сервер (D259, те же правила, что
 * у `make ops-bank-payments`). Отмечает оплату человек: сошедшиеся —
 * одной кнопкой с подтверждением на всю пачку, спорные — по одной.
 * Отметка пишется в журнал разбирательств на сотрудника (D79), поэтому
 * молча здесь не записывается ничего.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { getRestaurantTimeZone } from '@shell/format';
import { ErrorState, Loading } from '@shell/states';
import { rub } from '../../api';
import { Link } from '../../router';
import { Notice, isoDate } from '../../ui';
import { LIVE, plural, reconcileApi, sumKopecks } from './api';
import type { MarkAnswer, Plan as PlanData, PlanAnswer, PlanMark, PlanPartial } from './api';
import { ledgerPath, todayIso } from './paths';

export const planKey = ['reconcile', 'plan'] as const;

const invoices = (n: number) => `${n} ${plural(n, ['накладной', 'накладным', 'накладным'])}`;
const invoicesAcc = (n: number) => `${n} ${plural(n, ['накладную', 'накладные', 'накладных'])}`;

export function summaryText(plan: PlanData): string {
  const s = plan.summary;
  const head = `Долг в системе ${rub(s.open_total)} по ${invoices(s.open_count)}.`;
  if (s.marked_count === 0) return `${head} Всё, что оплачено по выписке, уже отмечено.`;
  return `${head} По выписке оплачено ещё ${invoicesAcc(s.marked_count)} на ${rub(
    s.marked_total,
  )} — после отметки долг ${rub(s.after_total)}.`;
}

type Result = { ok: boolean; text: string; errors?: string[] } | null;

export function PlanView({ canEdit, suppliers }: { canEdit: boolean; suppliers: { id: number; name: string }[] }) {
  const client = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const query = useQuery<PlanData, ApiError>({ queryKey: planKey, queryFn: () => reconcileApi.plan(), ...LIVE });

  const refreshed = (plan: PlanData) => {
    client.setQueryData(planKey, plan);
    client.invalidateQueries({ queryKey: ['reconcile', 'overview'] });
    client.invalidateQueries({ queryKey: ['reconcile', 'ledger'] });
    client.invalidateQueries({ queryKey: ['payments'] });
  };
  const failed = (error: ApiError) =>
    setResult({ ok: false, text: error.detail ?? 'Не получилось. Попробуйте ещё раз.' });

  const mark = useMutation<MarkAnswer, ApiError, number[]>({
    mutationFn: (ids) => reconcileApi.mark(ids),
    onSuccess: (answer) => {
      refreshed(answer.plan);
      setResult({ ok: answer.errors.length === 0, text: answer.message, errors: answer.errors });
    },
    onError: failed,
  });
  const markOne = useMutation<PlanAnswer, ApiError, { invoice_id: number; amount: string | null; paid_on: string }>({
    mutationFn: (body) => reconcileApi.markOne(body),
    onSuccess: (answer) => {
      refreshed(answer.plan);
      setResult({ ok: true, text: answer.message });
    },
    onError: failed,
  });
  const payee = useMutation<PlanAnswer, ApiError, { inn: string; supplier_id: number }>({
    mutationFn: (body) => reconcileApi.payee(body),
    onSuccess: (answer) => {
      refreshed(answer.plan);
      setResult({ ok: true, text: answer.message });
    },
    onError: failed,
  });

  if (query.isPending) return <Loading text="Раскладываю платежи по накладным…" />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const plan = query.data;
  const busy = mark.isPending || markOne.isPending || payee.isPending;
  const nothing =
    plan.marks.length === 0 &&
    plan.partial.length === 0 &&
    plan.unused.length === 0 &&
    plan.unknown.length === 0;

  return (
    <section className="rc-stack rc-plan" aria-label="Что в выписке">
      <div className="rc-card">
        <h2 className="rc-card__title">Что в выписке</h2>
        {plan.summary.period_to === null ? (
          <p className="rc-todo">
            Выписка ещё не загружена. Загрузите файл выше — система разложит платежи по накладным и
            покажет, что можно отметить оплаченным.
          </p>
        ) : (
          <>
            <p className="rc-summary">{summaryText(plan)}</p>
            <p className="faint">
              Выписка по {isoDate(plan.summary.period_to)}. Платежей поставщикам: {plan.summary.to_suppliers}
              {plan.summary.already_used > 0 ? `, из них уже учтены раньше: ${plan.summary.already_used}` : ''}.
            </p>
          </>
        )}
        {!canEdit && (
          <p className="rc-note">
            Отмечать оплаты может тот, у кого право «Оплаты · поставщики и выписка банка» на правку и право на деньги. Попросите
            владельца, если это ваша работа.
          </p>
        )}
        {result && (
          <Notice kind={result.ok ? 'ok' : 'error'}>
            {result.text}
            {result.errors && result.errors.length > 0 && (
              <span className="rc-errors">
                {result.errors.map((text) => (
                  <span key={text}>{text}</span>
                ))}
              </span>
            )}
          </Notice>
        )}
        {nothing && plan.summary.period_to !== null && (
          <p className="rc-todo">Разбирать нечего: всё, что есть в выписке, уже учтено.</p>
        )}
      </div>

      {plan.marks.length > 0 && (
        <Matched marks={plan.marks} canEdit={canEdit} busy={busy} onMark={(ids) => mark.mutate(ids)} />
      )}

      {plan.partial.length > 0 && (
        <div className="rc-card">
          <h3 className="block-title">Спорные — покрыто не полностью</h3>
          <p className="faint">
            Платёж нашёлся, но меньше остатка накладной. Если поставщик считает её оплаченной — «Оплачено
            полностью»; если доплата будет — «Оплачено частично» на сумму платежа.
          </p>
          <ul className="rc-plain-list rc-partial-list">
            {plan.partial.map((item) => (
              <PartialRow
                key={item.invoice_id}
                item={item}
                canEdit={canEdit}
                busy={busy}
                onMark={(body) => markOne.mutate(body)}
              />
            ))}
          </ul>
        </div>
      )}

      {(plan.unused.length > 0 || plan.unused_older > 0) && (
        <div className="rc-card">
          <h3 className="block-title">Платежи без накладной — скорее всего, её нет в складском учёте</h3>
          <p className="faint">
            Деньги ушли поставщику, а накладной на эту сумму в StoreHouse нет. Найдите бумагу и сдайте её
            фото в «Накладных» — или откройте карточку поставщика.
          </p>
          <ul className="rc-plain-list rc-unused">
            {plan.unused.map((item, index) => (
              <li key={`${item.day}-${item.amount}-${index}`} className="rc-unused__item">
                <span className="rc-unused__main">
                  <Link to={ledgerPath(item.supplier_id)}>{item.supplier}</Link>
                  <span className="faint">
                    {isoDate(item.day)} · {item.payee}
                  </span>
                  <span className="faint rc-purpose" title={item.purpose}>
                    {item.purpose}
                  </span>
                </span>
                <span className="num">
                  {rub(item.amount)}
                  {item.left !== item.amount && <span className="faint rc-sub">не легло {rub(item.left)}</span>}
                </span>
              </li>
            ))}
          </ul>
          {plan.unused_older > 0 && (
            <p className="faint">
              и ещё {plan.unused_older} {plural(plan.unused_older, ['старый', 'старых', 'старых'])} — в
              списке только свежие платежи
            </p>
          )}
        </div>
      )}

      {plan.unknown.length > 0 && (
        <div className="rc-card">
          <h3 className="block-title">Получатель похож на поставщика, но его нет в справочнике</h3>
          <p className="faint">
            Назначение платежа говорит «за товар», а ИНН получателя не знаком. Выберите, какой это
            поставщик, — ИНН запомнится, и в следующий раз платежи разложатся сами.
          </p>
          <ul className="rc-plain-list">
            {plan.unknown.map((item) => (
              <UnknownRow
                key={item.inn}
                item={item}
                suppliers={suppliers}
                canEdit={canEdit}
                busy={busy}
                onPick={(supplierId) => payee.mutate({ inn: item.inn, supplier_id: supplierId })}
              />
            ))}
          </ul>
        </div>
      )}

      {plan.learned_inn.length > 0 && (
        <div className="rc-card">
          <h3 className="block-title">ИНН из выписки</h3>
          <p className="faint">У этих поставщиков ИНН не был записан. Запомним его при отметке оплаты.</p>
          <ul className="rc-plain-list rc-inn">
            {plan.learned_inn.map((item) => (
              <li key={item.supplier_id}>
                {item.supplier} — <span className="num">{item.inn}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Matched({
  marks,
  canEdit,
  busy,
  onMark,
}: {
  marks: PlanMark[];
  canEdit: boolean;
  busy: boolean;
  onMark: (ids: number[]) => void;
}) {
  // Снятые галочки, а не поставленные: после обновления плана новые
  // строки приходят отмеченными, как и задумано (§3.2: «всё сразу»).
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [confirming, setConfirming] = useState(false);

  const groups = useMemo(() => {
    const map = new Map<number, { supplier: string; items: PlanMark[] }>();
    for (const item of marks) {
      const group = map.get(item.supplier_id) ?? { supplier: item.supplier, items: [] };
      group.items.push(item);
      map.set(item.supplier_id, group);
    }
    return [...map.entries()];
  }, [marks]);

  const picked = marks.filter((item) => !skipped.has(item.invoice_id));
  const total = sumKopecks(picked.map((item) => item.balance));
  const toggle = (id: number, on: boolean) => {
    setConfirming(false);
    setSkipped((current) => {
      const next = new Set(current);
      if (on) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="rc-card">
      <h3 className="block-title">Сошлось — платёж нашёлся</h3>
      <p className="faint">
        Снимите галочку, если платёж лёг не на ту накладную. Оплата запишется на каждую накладную днём
        платежа.
      </p>
      {groups.map(([supplierId, group]) => (
        <div key={supplierId} className="rc-group">
          <h4 className="rc-group__title">
            <Link to={ledgerPath(supplierId)}>{group.supplier}</Link>
            <span className="faint"> · {group.items.length} шт · {rub(sumKopecks(group.items.map((i) => i.balance)))}</span>
          </h4>
          <ul className="rc-plain-list">
            {group.items.map((item) => (
              <li key={item.invoice_id} className="rc-mark">
                <label className="rc-check rc-mark__check">
                  <input
                    type="checkbox"
                    checked={!skipped.has(item.invoice_id)}
                    disabled={!canEdit || busy}
                    onChange={(event) => toggle(item.invoice_id, event.target.checked)}
                    aria-label={`Накладная ${item.number}`}
                  />
                  <span className="rc-mark__main">
                    <span>
                      накл. {item.number} от {isoDate(item.doc_date)}
                      <span className="rc-how">{item.how}</span>
                    </span>
                    <span className="faint rc-purpose" title={item.payment.purpose}>
                      платёж {isoDate(item.payment.day)} на {rub(item.payment.amount)} · {item.payment.purpose}
                    </span>
                  </span>
                </label>
                <span className="num">{rub(item.balance)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {confirming ? (
        <div className="rc-confirm">
          <p>
            Отметить {invoicesAcc(picked.length)} на {rub(total)} оплаченными? Оплата запишется на вас,
            днём платежа из выписки.
          </p>
          <div className="rc-actions">
            <button
              type="button"
              className="button button--primary"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
                onMark(picked.map((item) => item.invoice_id));
              }}
            >
              Да, отметить
            </button>
            <button type="button" className="button" onClick={() => setConfirming(false)}>
              Отмена
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="button button--primary rc-mark-all"
          disabled={!canEdit || busy || picked.length === 0}
          onClick={() => setConfirming(true)}
        >
          Отметить сошедшиеся оплаченными ({picked.length} на {rub(total)})
        </button>
      )}
    </div>
  );
}

function PartialRow({
  item,
  canEdit,
  busy,
  onMark,
}: {
  item: PlanPartial;
  canEdit: boolean;
  busy: boolean;
  onMark: (body: { invoice_id: number; amount: string | null; paid_on: string }) => void;
}) {
  const today = todayIso(getRestaurantTimeZone());
  const [partial, setPartial] = useState(false);
  const [sum, setSum] = useState(item.covered);
  const [day, setDay] = useState(today);
  const valid = /^\d+([.,]\d{1,2})?$/.test(sum.trim()) && Number(sum.replace(',', '.')) > 0 && Boolean(day);

  return (
    <li className="rc-partial">
      <div className="rc-partial__line">
        <span className="rc-unused__main">
          <Link to={ledgerPath(item.supplier_id)}>{item.supplier}</Link>
          <span className="faint">
            накл. {item.number} от {isoDate(item.doc_date)} на {rub(item.balance)} · покрыто {rub(item.covered)}, остаток{' '}
            {rub(item.left)}
          </span>
        </span>
      </div>
      <div className="rc-actions">
        <button
          type="button"
          className="button button--small"
          disabled={!canEdit || busy}
          onClick={() => onMark({ invoice_id: item.invoice_id, amount: null, paid_on: today })}
        >
          Оплачено полностью
        </button>
        <button
          type="button"
          className="button button--small button--quiet"
          disabled={!canEdit || busy}
          aria-expanded={partial}
          onClick={() => setPartial((value) => !value)}
        >
          Оплачено частично…
        </button>
      </div>
      {partial && (
        <form
          className="rc-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!valid) return;
            onMark({ invoice_id: item.invoice_id, amount: sum.trim().replace(',', '.'), paid_on: day });
            setPartial(false);
          }}
        >
          <label className="rc-field">
            Сумма, ₽
            <input
              className="input input--num"
              inputMode="decimal"
              value={sum}
              onChange={(event) => setSum(event.target.value)}
              aria-label="Сколько оплачено"
            />
          </label>
          <label className="rc-field">
            Дата оплаты
            <input
              className="input"
              type="date"
              value={day}
              max={today}
              onChange={(event) => setDay(event.target.value)}
              aria-label="Дата оплаты"
            />
          </label>
          <button type="submit" className="button button--primary button--small" disabled={!valid || busy}>
            Записать
          </button>
        </form>
      )}
    </li>
  );
}

function UnknownRow({
  item,
  suppliers,
  canEdit,
  busy,
  onPick,
}: {
  item: PlanData['unknown'][number];
  suppliers: { id: number; name: string }[];
  canEdit: boolean;
  busy: boolean;
  onPick: (supplierId: number) => void;
}) {
  const [choice, setChoice] = useState('');
  return (
    <li className="rc-unknown">
      <span className="rc-unused__main">
        <span>{item.payee}</span>
        <span className="faint">
          ИНН {item.inn} · {item.count} {plural(item.count, ['платёж', 'платежа', 'платежей'])} на {rub(item.total)}
        </span>
      </span>
      <span className="rc-inline-form">
        <select
          className="select select--small"
          value={choice}
          disabled={!canEdit || busy}
          onChange={(event) => setChoice(event.target.value)}
          aria-label={`Какой это поставщик: ${item.payee}`}
        >
          <option value="">Выберите поставщика…</option>
          {suppliers.map((supplier) => (
            <option key={supplier.id} value={supplier.id}>
              {supplier.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="button button--small"
          disabled={!canEdit || busy || !choice}
          onClick={() => onPick(Number(choice))}
        >
          Это поставщик
        </button>
      </span>
    </li>
  );
}
